import { ReloadOutlined } from '@ant-design/icons';
import { Button, Spin, Table } from 'antd';
import { observer } from 'mobx-react';
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    Bar,
    BarChart,
    CartesianGrid,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { KpiCardShell } from '~analytics-components/kpi-card-shell';
import type { KpiTileData } from '~analytics-components/kpi-tile';
import { SectionHeader } from '~analytics-components/section-header';
import { formatRate } from '~containers/analytics/kpis/format-rate';
import { BoxedTileRow } from '~containers/analytics/kpis/forms/boxed-tile-row';
import {
    MultilineChart,
    MultilineLegend,
} from '~containers/analytics/kpis/forms/multiline-chart';
import {
    BLUE_700,
    ORANGE_800,
    UNKNOWN_CT_BLUE_OPACITY_15,
} from '~styles/colors';
import { useContestStore } from '../../../contexts/mobx';
import type {
    ContestStats,
    ContestStatsParticipant,
} from '../../../stores/contest';
import { formatAppTargets } from '../../../utils/app-target';
import { convertUtcToLocalMoment } from '../../../utils/timezone';
import { CHANNEL_LABELS } from '../entries-tab/labels';
import { ShareBars } from './share-bars';
import { SplitBar } from './split-bar';
import {
    ABUSE_REFUSALS,
    blockSections,
    codeRefusalRate,
    entriesOn,
    ORIGIN_LABELS,
    periodStatus,
    PHOTO_REVIEW_LABELS,
    REFUSAL_LABELS,
    share,
    todayTrend,
    topRefusals,
} from './stats';
import { useContestStats } from './use-contest-stats';
import { WeekHourHeatmap } from './week-hour-heatmap';
import style from './style.module.scss';

const DAILY_SERIES = [
    { label: 'Entries', color: BLUE_700 },
    { label: 'New participants', color: ORANGE_800 },
];

const RECIPIENT_LABELS = { retailer: 'Retailer', sponsor: 'Sponsor' };
const CONSENT_CHANNEL_LABELS = { email: 'Email', sms: 'SMS' };

const Section = ({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) => (
    <section className={style.section}>
        <SectionHeader title={title} />
        <div className={style.grid}>{children}</div>
    </section>
);

function uncountedText(stats: ContestStats): string | undefined {
    const uncounted = stats.referral.uncountedEntries.reduce(
        (total, row) => total + row.count,
        0
    );
    return uncounted > 0
        ? `${uncounted.toLocaleString()} more friends brought no chance, held back by a cap or the skill-testing question.`
        : undefined;
}

const EntriesPerParticipantChart = ({
    ranges,
}: {
    ranges: ContestStats['entriesPerParticipant'];
}) => (
    <ResponsiveContainer width="100%" height={200}>
        <BarChart
            data={ranges.map((row) => ({
                range: `${row.range} ${
                    row.range === '1' ? 'entry' : 'entries'
                }`,
                participants: row.participants,
            }))}
            margin={{ top: 20, right: 8, left: 0, bottom: 4 }}
        >
            <CartesianGrid
                vertical={false}
                strokeDasharray="4 4"
                stroke={UNKNOWN_CT_BLUE_OPACITY_15}
            />
            <XAxis dataKey="range" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 10 }} width="auto" allowDecimals={false} />
            <Tooltip
                cursor={{ fill: UNKNOWN_CT_BLUE_OPACITY_15 }}
                formatter={(value) => [value, 'Participants']}
            />
            <Bar
                dataKey="participants"
                fill={BLUE_700}
                radius={[4, 4, 0, 0]}
                maxBarSize={48}
            >
                <LabelList
                    dataKey="participants"
                    position="top"
                    fontSize={11}
                />
            </Bar>
        </BarChart>
    </ResponsiveContainer>
);

/** Each participant opens their entries in the Entries tab. */
const ParticipantTable = ({
    contestId,
    rows,
}: {
    contestId: number;
    rows: ContestStatsParticipant[];
}) => (
    <Table<ContestStatsParticipant>
        size="small"
        rowKey="participantId"
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: 'No participant yet.' }}
        columns={[
            {
                title: 'Participant',
                key: 'participantId',
                render: (_, row) => (
                    <Link
                        to={`/contest/edit/${contestId}/entries?search=${row.participantId}`}
                    >
                        {row.participantId.slice(0, 8)}
                        {row.anonymized && ' (anonymised)'}
                    </Link>
                ),
            },
            {
                title: 'Entries',
                key: 'entries',
                align: 'right',
                render: (_, row) => row.entries.toLocaleString(),
            },
            {
                title: 'Chances',
                key: 'chances',
                align: 'right',
                render: (_, row) => row.chances.toLocaleString(),
            },
            {
                title: 'Friends',
                key: 'friends',
                align: 'right',
                render: (_, row) => row.friends.toLocaleString(),
            },
        ]}
    />
);

export const StatsTab = observer(() => {
    const contestId = Number(useParams<{ id: string }>().id);
    const contest = useContestStore().contest;
    const { stats, isLoading, failed, refresh } = useContestStats(contestId);

    if (!stats) {
        return (
            <div className={style.placeholder}>
                {failed ? (
                    <>
                        <p>The statistics could not be loaded.</p>
                        <Button onClick={refresh}>Try again</Button>
                    </>
                ) : (
                    <Spin />
                )}
            </div>
        );
    }

    const period = contest
        ? periodStatus(contest, new Date(), stats.timeZone)
        : null;
    const isRunning = contest?.state === 'running';
    const sections = blockSections(stats);

    const followUp: KpiTileData[] = [
        ...stats.photoReviews.map((row) => ({
            title: `Photos: ${PHOTO_REVIEW_LABELS[row.status].toLowerCase()}`,
            value: row.count,
        })),
        ...(sections.messages
            ? [
                  {
                      title: 'Contact messages',
                      value: stats.messages.received,
                  },
                  {
                      title: 'Messages not sent',
                      value: stats.messages.failed,
                  },
              ]
            : []),
        {
            title: 'Anonymised participants',
            value: stats.totals.anonymizedParticipants,
        },
    ];

    return (
        <div className={style.dashboard}>
            <div className={style.toolbar}>
                <span className={style.computed_at}>
                    Updated{' '}
                    {convertUtcToLocalMoment(
                        stats.computedAt,
                        stats.timeZone
                    )?.format('lll')}
                    {' · '}days and hours in {stats.timeZone}
                </span>
                <Button
                    icon={<ReloadOutlined />}
                    loading={isLoading}
                    onClick={refresh}
                >
                    Refresh
                </Button>
            </div>

            <BoxedTileRow
                highlightIndex={0}
                columns={5}
                tiles={[
                    {
                        title: 'Entries',
                        value: stats.totals.entries,
                        description: `${stats.totals.chances.toLocaleString()} chances in the draw: an entry can be worth more than one.`,
                    },
                    {
                        title: 'Participants',
                        value: stats.totals.participants,
                        description: `${stats.totals.enteringParticipants.toLocaleString()} of them have entered at least once.`,
                    },
                    isRunning
                        ? {
                              title: 'Entries today',
                              value: entriesOn(stats, stats.today),
                              trend: todayTrend(stats),
                          }
                        : {
                              title: 'Entries per day',
                              value:
                                  stats.days.length > 0
                                      ? stats.days.reduce(
                                            (total, day) => total + day.entries,
                                            0
                                        ) / stats.days.length
                                      : 0,
                              maximumFractionDigits: 1,
                              description:
                                  'The average over the contest days so far.',
                          },
                    {
                        title: 'No-purchase share',
                        value:
                            share(
                                stats.origins.find(
                                    (row) => row.origin === 'no_purchase'
                                )?.entries ?? 0,
                                stats.totals.entries
                            ) ?? 0,
                        isPercent: true,
                        description:
                            'Entries made without a purchase, which the rules require the contest to offer.',
                    },
                    period
                        ? { title: period.title, value: period.value }
                        : { title: 'Days left', value: 'N/A' },
                ]}
            />

            <Section title="Activity">
                <KpiCardShell
                    size="lg"
                    title="Entries and new participants per day"
                    subtitle={
                        stats.days.length > 0
                            ? undefined
                            : 'The contest has not started.'
                    }
                >
                    {stats.days.length > 0 && (
                        <>
                            <MultilineLegend
                                unit="count"
                                series={DAILY_SERIES.map((series) => ({
                                    ...series,
                                    value: stats.days.reduce(
                                        (total, day) =>
                                            total +
                                            (series.label === 'Entries'
                                                ? day.entries
                                                : day.newParticipants),
                                        0
                                    ),
                                }))}
                            />
                            <MultilineChart
                                unit="count"
                                chartStyle="area"
                                series={DAILY_SERIES}
                                rows={stats.days.map((day) => ({
                                    date: day.day,
                                    Entries: day.entries,
                                    'New participants': day.newParticipants,
                                }))}
                            />
                        </>
                    )}
                </KpiCardShell>
                <KpiCardShell
                    size="lg"
                    title="When people enter"
                    subtitle="Entries by weekday and hour: the darker, the busier. A good time to send a reminder."
                >
                    <WeekHourHeatmap weekHours={stats.weekHours} />
                </KpiCardShell>
            </Section>

            <Section title="How people enter">
                <KpiCardShell size="md" title="Entries by path">
                    <ShareBars
                        empty="No entry yet."
                        bars={stats.origins.map((row) => ({
                            key: row.origin,
                            label: ORIGIN_LABELS[row.origin],
                            value: row.entries,
                            detail:
                                row.chances !== row.entries
                                    ? `${row.chances.toLocaleString()} chances`
                                    : undefined,
                        }))}
                    />
                </KpiCardShell>
                <KpiCardShell
                    size="md"
                    title="Entries per participant"
                    subtitle={`${formatRate(
                        share(
                            stats.totals.returningParticipants,
                            stats.totals.enteringParticipants
                        )
                    )} of participants came back on another day.`}
                >
                    <EntriesPerParticipantChart
                        ranges={stats.entriesPerParticipant}
                    />
                </KpiCardShell>
                <KpiCardShell
                    size="md"
                    title="From view to entry"
                    info="Views count page loads: one person who opens the contest twice counts twice."
                >
                    <ShareBars
                        empty="Nothing counted yet."
                        base={stats.views.contestViews || undefined}
                        bars={[
                            {
                                key: 'views',
                                label: 'Contest views',
                                value: stats.views.contestViews,
                                isBase: true,
                            },
                            {
                                key: 'participants',
                                label: 'Signed up',
                                value: stats.totals.participants,
                            },
                            {
                                key: 'entering',
                                label: 'Entered at least once',
                                value: stats.totals.enteringParticipants,
                            },
                        ]}
                    />
                    <p className={style.note}>
                        Entries per view:{' '}
                        {stats.views.contestViews > 0
                            ? (
                                  stats.totals.entries /
                                  stats.views.contestViews
                              ).toFixed(2)
                            : 'N/A'}
                        {' · '}
                        {stats.views.shareClicks.toLocaleString()} shares
                    </p>
                </KpiCardShell>
                {sections.referral && (
                    <KpiCardShell size="md" title="Referral">
                        <BoxedTileRow
                            highlightIndex={0}
                            columns={2}
                            tiles={[
                                {
                                    title: 'Friends signed up',
                                    value: stats.referral.referredParticipants,
                                },
                                {
                                    title: 'Active referrers',
                                    value: stats.referral.referrers,
                                },
                                {
                                    title: 'Friends per referrer',
                                    value:
                                        stats.referral.referrers > 0
                                            ? stats.referral
                                                  .referredParticipants /
                                              stats.referral.referrers
                                            : 0,
                                    maximumFractionDigits: 1,
                                },
                                {
                                    title: 'Referral entries',
                                    value: stats.referral.countedEntries,
                                    description: uncountedText(stats),
                                },
                            ]}
                        />
                    </KpiCardShell>
                )}
            </Section>

            <Section title="What fails">
                {sections.codes && (
                    <KpiCardShell
                        size="md"
                        title="Refused proof-of-purchase codes"
                        subtitle="Of every code typed. A high rate points at the packaging or the instructions."
                    >
                        <BoxedTileRow
                            highlightIndex={0}
                            tiles={[
                                {
                                    title: 'Refusal rate',
                                    value:
                                        codeRefusalRate(stats.codes) ?? 'N/A',
                                    isPercent: true,
                                },
                                {
                                    title: 'Invalid',
                                    value: stats.codes.invalid,
                                },
                                {
                                    title: 'Already used',
                                    value: stats.codes.alreadyUsed,
                                },
                            ]}
                        />
                    </KpiCardShell>
                )}
                <KpiCardShell size="md" title="Refused attempts by reason">
                    <ShareBars
                        empty="No attempt refused."
                        color={ORANGE_800}
                        bars={topRefusals(stats.refusals)}
                    />
                </KpiCardShell>
            </Section>

            <Section title="Audience">
                <KpiCardShell size="md" title="Participants by province">
                    <ShareBars
                        empty="No participant yet."
                        bars={stats.provinces.map((row) => ({
                            key: row.province,
                            label: row.province,
                            value: row.participants,
                        }))}
                    />
                </KpiCardShell>
                <KpiCardShell
                    size="md"
                    title="Participants by postal code area"
                    subtitle="The largest areas, by the postal code's first three characters."
                >
                    <ShareBars
                        empty="No postal code yet."
                        base={stats.regions.participantsWithPostalCode}
                        bars={stats.regions.areas.map((row) => ({
                            key: row.area,
                            label: row.area,
                            value: row.participants,
                        }))}
                    />
                </KpiCardShell>
                <KpiCardShell size="sm" title="Language">
                    <SplitBar
                        empty="No participant yet."
                        parts={stats.languages.map((row) => ({
                            key: row.lang,
                            label: row.lang === 'fr' ? 'French' : 'English',
                            value: row.participants,
                        }))}
                    />
                </KpiCardShell>
                <KpiCardShell size="sm" title="Banner">
                    <SplitBar
                        empty="No participant yet."
                        parts={stats.appTargets.map((row) => ({
                            key: row.appTarget ?? 'unknown',
                            label: row.appTarget
                                ? formatAppTargets([row.appTarget])
                                : 'Not declared',
                            value: row.participants,
                            unknown: row.appTarget === null,
                        }))}
                    />
                </KpiCardShell>
                <KpiCardShell size="md" title="Channel">
                    <SplitBar
                        empty="No participant yet."
                        parts={stats.channels.map((row) => ({
                            key: row.channel ?? 'unknown',
                            label: row.channel
                                ? CHANNEL_LABELS[row.channel]
                                : 'Not declared',
                            value: row.participants,
                            unknown: row.channel === null,
                        }))}
                    />
                </KpiCardShell>
            </Section>

            {stats.consents.length > 0 && (
                <Section title="Consents">
                    <KpiCardShell
                        size="lg"
                        title="Consent rate by purpose"
                        subtitle="Of the participants who saw the checkbox. A withdrawal no longer counts as a consent."
                    >
                        <ShareBars
                            empty="No consent answered yet."
                            widthBy="share"
                            bars={stats.consents.map((row) => ({
                                key: `${row.recipient}-${row.channel}`,
                                label: `${RECIPIENT_LABELS[row.recipient]} · ${
                                    CONSENT_CHANNEL_LABELS[row.channel]
                                }`,
                                value: row.granted,
                                of: row.answered,
                                detail:
                                    row.withdrawn > 0
                                        ? `${row.withdrawn.toLocaleString()} withdrawn`
                                        : undefined,
                            }))}
                        />
                    </KpiCardShell>
                </Section>
            )}

            <Section title="Anti-fraud">
                <KpiCardShell
                    size="lg"
                    title="Attempts blocked"
                    subtitle="Refusals that scripts and repeated attempts produce."
                >
                    <BoxedTileRow
                        highlightIndex={-1}
                        tiles={ABUSE_REFUSALS.map((reason) => ({
                            title: REFUSAL_LABELS[reason],
                            value:
                                stats.refusals.find(
                                    (row) => row.reason === reason
                                )?.count ?? 0,
                        }))}
                    />
                </KpiCardShell>
                <KpiCardShell
                    size="md"
                    title="Participants with the most chances"
                    subtitle={`They hold ${formatRate(
                        share(
                            stats.topByChances.reduce(
                                (total, row) => total + row.chances,
                                0
                            ),
                            stats.totals.chances
                        )
                    )} of all chances.`}
                >
                    <ParticipantTable
                        contestId={contestId}
                        rows={stats.topByChances}
                    />
                </KpiCardShell>
                {sections.referral && (
                    <KpiCardShell
                        size="md"
                        title="Participants who brought the most friends"
                    >
                        <ParticipantTable
                            contestId={contestId}
                            rows={stats.topByFriends}
                        />
                    </KpiCardShell>
                )}
            </Section>

            <Section title="Follow-up">
                <KpiCardShell size="lg" title="What is left to handle">
                    <BoxedTileRow
                        highlightIndex={-1}
                        columns={Math.min(followUp.length, 4)}
                        tiles={followUp}
                    />
                </KpiCardShell>
            </Section>
        </div>
    );
});
